import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface ImpactStats {
  co2_saved: number;
  waste_reduced: number;
  trees_planted: number;
  water_saved: number;
  energy_saved: number;
  total_users: number;
  total_missions_completed: number;
}

const mockStats: ImpactStats = {
  co2_saved: 12847.5,
  waste_reduced: 8924.2,
  trees_planted: 1247,
  water_saved: 445720,
  energy_saved: 9847.3,
  total_users: 50000,
  total_missions_completed: 2300000
};

export function useImpactStats() {
  const [stats, setStats] = useState<ImpactStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      if (!isSupabaseConfigured) {
        console.log('Using mock impact stats data');
        setStats(mockStats);
        setLoading(false);
        return;
      }

      console.log('Fetching impact stats from database...');
      
      const { data, error } = await supabase
        .from('impact_stats')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error fetching impact stats:', error);
        
        // If no data exists, create initial record
        if (error.code === 'PGRST116' || !data || data.length === 0) {
          console.log('Creating initial impact stats...');
          
          const { data: newData, error: insertError } = await supabase
            .from('impact_stats')
            .insert(mockStats)
            .select()
            .single();

          if (insertError) {
            console.error('Error creating impact stats:', insertError);
            setStats(mockStats);
            return;
          }

          setStats(newData);
          return;
        }
        
        setStats(mockStats);
        return;
      }

      if (!data || data.length === 0) {
        console.log('No impact stats found, creating initial record...');
        
        const { data: newData, error: insertError } = await supabase
          .from('impact_stats')
          .insert(mockStats)
          .select()
          .single();

        if (insertError) {
          console.error('Error creating impact stats:', insertError);
          setStats(mockStats);
          return;
        }

        setStats(newData);
        return;
      }

      setStats(data[0]);
    } catch (error) {
      console.error('Error fetching impact stats:', error);
      setStats(mockStats);
    } finally {
      setLoading(false);
    }
  };

  return {
    stats,
    loading,
    refetch: fetchStats
  };
}